import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { MapContainer, TileLayer, Marker, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Search, MapPin, Heart, Leaf,
  Loader2, X, ExternalLink, Star, Building2, Navigation,
  ArrowUpDown, List, Map as MapIcon, ChevronDown, Info, Target, Globe, Shield,
  Sparkles, TrendingUp, Award, CreditCard, DollarSign, ArrowRight, CheckCircle2
} from 'lucide-react';
import { COLORS } from '../../../constants/dashboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DISTANCE_OPTIONS = [
  { value: 25, label: '< 25 mi' },
  { value: 50, label: '< 50 mi' },
  { value: 100, label: '< 100 mi' },
  { value: 200, label: '< 200 mi' },
  { value: 0, label: 'Any' },
];

const SORT_OPTIONS = [
  { value: 'score', label: 'Best Match', icon: Star },
  { value: 'distance', label: 'Closest', icon: Navigation },
  { value: 'name', label: 'A-Z', icon: ArrowUpDown },
];

const CATEGORY_OPTIONS = [
  { id: 'education', label: 'Education' },
  { id: 'healthcare', label: 'Health' },
  { id: 'environmental', label: 'Environment' },
  { id: 'animal', label: 'Animals' },
  { id: 'food', label: 'Food & Hunger' },
  { id: 'arts', label: 'Arts & Culture' },
  { id: 'community', label: 'Community' },
  { id: 'youth', label: 'Youth' },
];

const REASON_STYLES = {
  geo: { bg: 'bg-sky-50', text: 'text-sky-700', border: 'border-sky-200', icon: Navigation, gradient: 'from-sky-500 to-blue-600' },
  csr: { bg: 'bg-sky-50', text: 'text-sky-700', border: 'border-sky-200', icon: Heart, gradient: 'from-sky-500 to-blue-600' },
  esg: { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200', icon: Leaf, gradient: 'from-violet-500 to-purple-600' },
  size: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: Building2, gradient: 'from-amber-500 to-orange-600' },
  keyword: { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', icon: Search, gradient: 'from-rose-500 to-pink-600' },
};

/* Fix default Leaflet marker icons */
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const createScoreIcon = (score) => {
  const color = score >= 70 ? '#38bdf8' : score >= 40 ? '#f59e0b' : '#94a3b8';
  return L.divIcon({
    className: 'custom-score-marker',
    html: `<div style="width:28px;height:28px;background:${color};border:2.5px solid #fff;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,0.25);font-size:10px;font-weight:800;color:#fff;cursor:pointer;">${score}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
};

const officeIcon = L.divIcon({
  className: 'custom-office-marker',
  html: `<div style="width:34px;height:34px;background:#1e3a5f;border:3px solid #fff;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 10px rgba(0,0,0,0.35);">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/></svg>
  </div>`,
  iconSize: [34, 34],
  iconAnchor: [17, 17],
});

/* Fit bounds once */
const MapController = ({ charities, officeCoords }) => {
  const map = useMap();
  const hasFitted = useRef(false);
  useEffect(() => {
    if (hasFitted.current) return;
    const points = [];
    if (officeCoords?.lat && officeCoords?.lon) points.push([officeCoords.lat, officeCoords.lon]);
    charities.forEach(c => { if (c.latitude && c.longitude) points.push([c.latitude, c.longitude]); });
    if (points.length > 1) {
      map.fitBounds(L.latLngBounds(points), { padding: [30, 30], maxZoom: 12 });
      hasFitted.current = true;
    } else if (points.length === 1) {
      map.setView(points[0], 10);
      hasFitted.current = true;
    }
  }, [charities, officeCoords, map]);
  return null;
};

/* ─── Score Ring ─── */
const ScoreRing = ({ score, size = 52, showLabel = false }) => {
  const radius = size * 0.38;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 70 ? '#38bdf8' : score >= 40 ? '#f59e0b' : '#94a3b8';
  const bgColor = 'var(--bg-subtle)';
  return (
    <div className="flex flex-col items-center flex-shrink-0">
      {showLabel && <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-0.5">Match</span>}
      <div className="relative" style={{ width: size, height: size }}>
        <svg className="-rotate-90" style={{ width: size, height: size }} viewBox={`0 0 ${size} ${size}`}>
          <circle cx={size/2} cy={size/2} r={radius} fill={bgColor} stroke="var(--border-subtle)" strokeWidth="3" />
          <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={color} strokeWidth="3.5" strokeLinecap="round"
            strokeDasharray={circumference} strokeDashoffset={offset}
            style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-xs font-extrabold" style={{ color }}>{score}%</span>
      </div>
    </div>
  );
};

/* ─── Profile Summary ─── */
const ProfileSummary = ({ bizContext }) => {
  const [showFormula, setShowFormula] = useState(false);
  if (!bizContext) return null;
  const causes = bizContext.causes || [];
  const esg = bizContext.esg || [];
  const geoFocus = bizContext.geographic_focus || [];
  const city = bizContext.city || '';
  const state = bizContext.state || '';
  const goToBranding = () => window.dispatchEvent(new CustomEvent('navigate-to', { detail: 'settings-branding' }));

  return (
    <div className="space-y-2" data-testid="profile-summary-panel">
      <div className="bg-gradient-to-br from-slate-50 to-blue-50 border border-slate-200 rounded-xl px-3 py-3">
        <div className="flex items-center justify-between mb-2.5">
          <div className="flex items-center gap-1.5">
            <div className="w-5 h-5 rounded-md bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <Target className="w-3 h-3 text-white" />
            </div>
            <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">Your Goals</span>
          </div>
          <button data-testid="edit-goals-btn" onClick={goToBranding}
            className="text-[10px] font-bold text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-2 py-0.5 rounded-md transition-colors">
            Edit
          </button>
        </div>
        <div className="space-y-2">
          {causes.length > 0 && (
            <div className="flex flex-wrap items-center gap-1">
              <Heart className="w-3 h-3 text-sky-500 flex-shrink-0" />
              {causes.map(c => (
                <span key={c} className="px-1.5 py-px rounded-md text-[9px] font-semibold bg-sky-100 text-sky-700">{c.replace(/_/g, ' ')}</span>
              ))}
            </div>
          )}
          {esg.length > 0 && (
            <div className="flex flex-wrap items-center gap-1">
              <Shield className="w-3 h-3 text-violet-500 flex-shrink-0" />
              {esg.map(e => (
                <span key={e} className="px-1.5 py-px rounded-md text-[9px] font-semibold bg-violet-100 text-violet-700">{e.replace(/_/g, ' ')}</span>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-1">
            <Globe className="w-3 h-3 text-blue-500 flex-shrink-0" />
            {geoFocus.length > 0 ? geoFocus.map(g => (
              <span key={g} className="px-1.5 py-px rounded-md text-[9px] font-semibold bg-blue-100 text-blue-700">{g.replace(/_/g, ' ')}</span>
            )) : <span className="text-[9px] text-gray-400">Not set</span>}
            {city && <span className="px-1.5 py-px rounded-md text-[9px] font-semibold bg-slate-200 text-slate-600">HQ: {city}{state ? `, ${state}` : ''}</span>}
          </div>
        </div>
      </div>
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl px-3 py-2.5">
        <button data-testid="scoring-formula-toggle" onClick={() => setShowFormula(!showFormula)}
          className="flex items-center gap-1.5 text-[11px] font-bold text-amber-700 hover:text-amber-900 transition-colors w-full">
          <Sparkles className="w-3.5 h-3.5" />
          <span>How scoring works</span>
          <ChevronDown className={`w-3 h-3 ml-auto transition-transform ${showFormula ? 'rotate-180' : ''}`} />
        </button>
        {showFormula && (
          <div className="mt-2 text-[10px] space-y-1.5 pb-1" data-testid="scoring-formula-detail">
            <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-sky-500" /><span className="text-slate-600"><strong className="text-slate-800">Geographic</strong> up to 35 pts</span></div>
            <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-sky-500" /><span className="text-slate-600"><strong className="text-slate-800">CSR Match</strong> up to 40 pts</span></div>
            <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-violet-500" /><span className="text-slate-600"><strong className="text-slate-800">ESG</strong> up to 25 pts</span></div>
            <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-amber-500" /><span className="text-slate-600"><strong className="text-slate-800">Size</strong> up to 10 pts</span></div>
            <p className="text-[9px] text-amber-600 pt-1 border-t border-amber-200">Max 100. Closer + aligned = higher.</p>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Charity Card ─── */
const CharityCard = ({ charity, onSelect, compact = false, isHighlighted = false }) => {
  const scoreColor = charity.score >= 70 ? 'sky' : charity.score >= 40 ? 'amber' : 'slate';
  const scoreGradient = charity.score >= 70 ? 'from-sky-500 to-blue-600' : charity.score >= 40 ? 'from-amber-500 to-orange-500' : 'from-slate-400 to-slate-500';

  return (
    <div
      className={`group rounded-xl border transition-all cursor-pointer relative overflow-hidden ${
        isHighlighted ? 'ring-2 ring-blue-400 border-blue-300 shadow-lg' :
        'border-gray-200 hover:border-blue-300 hover:shadow-lg'
      } ${compact ? 'p-3' : 'p-4'}`}
      style={{ background: 'var(--bg-surface)' }}
      data-testid={`discover-card-${charity.ein}`}
      onClick={() => onSelect(charity)}
    >
      {/* Top accent bar */}
      <div className={`absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r ${scoreGradient} opacity-60 group-hover:opacity-100 transition-opacity`} />

      <div className="flex items-start gap-3">
        <ScoreRing score={charity.score} size={compact ? 42 : 52} showLabel={!compact} />
        <div className="flex-1 min-w-0">
          <h4 className={`font-bold text-gray-900 leading-tight group-hover:text-blue-700 transition-colors ${compact ? 'text-xs truncate' : 'text-sm'}`}>
            {charity.name}
          </h4>
          <div className="flex items-center gap-1.5 mt-1">
            <MapPin className="w-3 h-3 text-gray-400 flex-shrink-0" />
            <span className="text-xs text-gray-500 truncate">
              {charity.city}{charity.city && charity.state ? ', ' : ''}{charity.state}
            </span>
            {charity.distance_miles != null && (
              <span className="px-1.5 py-px rounded-full text-[10px] font-bold bg-sky-100 text-sky-700 flex-shrink-0">
                {charity.distance_miles} mi
              </span>
            )}
          </div>
          {!compact && charity.category !== 'Unknown' && (
            <span className="inline-block mt-1.5 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-slate-100 text-slate-600">{charity.category}</span>
          )}
        </div>
      </div>

      {charity.reasons?.length > 0 && (
        <div className={`flex flex-wrap gap-1 ${compact ? 'mt-2' : 'mt-3'}`}>
          {charity.reasons.slice(0, compact ? 2 : 3).map((reason, i) => {
            const style = REASON_STYLES[reason.type] || REASON_STYLES.keyword;
            const Icon = style.icon;
            return (
              <span key={i} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${style.bg} ${style.text} ${style.border}`}>
                <Icon className="w-2.5 h-2.5" />{reason.text}
              </span>
            );
          })}
        </div>
      )}

      {!compact && charity.income > 0 && (
        <div className="mt-2.5 flex items-center gap-1.5">
          <TrendingUp className="w-3 h-3 text-gray-300" />
          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full bg-gradient-to-r ${scoreGradient}`}
              style={{ width: `${Math.min((Math.log10(Math.max(charity.income, 1)) / 9) * 100, 100)}%` }} />
          </div>
          <span className="text-[10px] text-gray-400 flex-shrink-0 font-medium">
            ${charity.income >= 1e9 ? `${(charity.income/1e9).toFixed(1)}B` :
              charity.income >= 1e6 ? `${(charity.income/1e6).toFixed(1)}M` :
              charity.income >= 1e3 ? `${(charity.income/1e3).toFixed(0)}K` :
              charity.income.toLocaleString()}
          </span>
        </div>
      )}
    </div>
  );
};

/* ═══════════════ MAIN VIEW ═══════════════ */
const CharityDiscoverView = ({ businessProfile, onSelectCharity, onDonate }) => {
  const [query, setQuery] = useState('');
  const [charities, setCharities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [radiusMiles, setRadiusMiles] = useState(0);
  const [sortBy, setSortBy] = useState('score');
  const [viewMode, setViewMode] = useState('list');
  const [filters, setFilters] = useState({ csr_match: false, esg_match: false, category: null });
  const [bizContext, setBizContext] = useState(null);
  const [selectedCharity, setSelectedCharity] = useState(null);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [visibleCount, setVisibleCount] = useState(30);
  const [defaultRadiusSet, setDefaultRadiusSet] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const sortDropdownRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (sortDropdownRef.current && !sortDropdownRef.current.contains(e.target)) setSortDropdownOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchCharities = useCallback(async (overrideQuery, overrideRadius, pageNum = 0, append = false, overrideSort) => {
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const params = new URLSearchParams();
      const q = overrideQuery !== undefined ? overrideQuery : query;
      const r = overrideRadius !== undefined ? overrideRadius : radiusMiles;
      const s = overrideSort !== undefined ? overrideSort : sortBy;
      if (q) params.append('q', q);
      params.append('radius_miles', r);
      params.append('page', pageNum);
      params.append('sort_by', s);
      if (filters.csr_match) params.append('csr_match', 'true');
      if (filters.esg_match) params.append('esg_match', 'true');
      if (filters.category) params.append('category', filters.category);
      const res = await axios.get(`${BACKEND_URL}/api/charities/discover?${params.toString()}`, { withCredentials: true });
      const newCharities = res.data.charities || [];
      if (append) {
        setCharities(prev => { const eins = new Set(prev.map(c => c.ein)); return [...prev, ...newCharities.filter(c => !eins.has(c.ein))]; });
        setHasMore(newCharities.length >= 20);
      } else {
        setCharities(newCharities);
        setBizContext(res.data.business_context || null);
        setVisibleCount(100);
        setCurrentPage(0);
        setHasMore(newCharities.length >= 20);
        if (!defaultRadiusSet && res.data.business_context?.geographic_focus) {
          const gf = res.data.business_context.geographic_focus;
          let dr = 50;
          if (gf.includes('international') || gf.includes('national')) dr = 0;
          else if (gf.includes('state')) dr = 200;
          setRadiusMiles(dr);
          setDefaultRadiusSet(true);
        }
      }
    } catch (err) { console.error('Discover error:', err); }
    finally { setLoading(false); setLoadingMore(false); }
  }, [query, radiusMiles, filters, sortBy]);

  useEffect(() => { fetchCharities(); }, []);
  useEffect(() => { fetchCharities(); }, [radiusMiles, filters]);

  const handleQueryChange = (val) => { setQuery(val); if (searchTimeout) clearTimeout(searchTimeout); setSearchTimeout(setTimeout(() => fetchCharities(val), 600)); };
  const handleRadiusChange = (r) => { setRadiusMiles(r); fetchCharities(undefined, r); };
  const handleSortChange = (s) => { setSortBy(s); setSortDropdownOpen(false); fetchCharities(undefined, undefined, 0, false, s); };
  const loadMore = () => { const next = currentPage + 1; setCurrentPage(next); fetchCharities(undefined, undefined, next, true); };
  const toggleFilter = (key) => setFilters(prev => ({ ...prev, [key]: !prev[key] }));
  const setCategory = (cat) => setFilters(prev => ({ ...prev, category: prev.category === cat ? null : cat }));

  const placeholders = ['food banks near us', 'cancer research', 'environmental nonprofits', 'animal shelters', 'STEM education', 'veterans support'];
  const [phIdx, setPhIdx] = useState(0);
  useEffect(() => { const i = setInterval(() => setPhIdx(p => (p + 1) % placeholders.length), 3000); return () => clearInterval(i); }, []);

  const officeCity = bizContext?.city || businessProfile?.address_city || '';
  const officeState = bizContext?.state || '';
  const geoFocus = bizContext?.geographic_focus || [];
  const isNationwide = geoFocus.includes('national') || geoFocus.includes('international');
  const causes = bizContext?.causes || [];

  const subtitle = (() => {
    const parts = [];
    if (isNationwide) { parts.push('Nationwide'); if (officeCity) parts[0] += ` (HQ: ${officeCity}${officeState ? `, ${officeState}` : ''})`; }
    else if (officeCity) parts.push(`Near ${officeCity}${officeState ? `, ${officeState}` : ''}`);
    if (causes.length > 0) parts.push(causes.slice(0, 3).map(c => c.replace(/_/g, ' ')).join(', '));
    return parts.join(' · ') || 'Personalized for your company';
  })();

  const currentSortOption = SORT_OPTIONS.find(o => o.value === sortBy) || SORT_OPTIONS[0];
  const officeCoords = bizContext ? { lat: bizContext.office_lat, lon: bizContext.office_lon } : null;
  const mappableCharities = charities.filter(c => c.latitude && c.longitude);
  const topScore = charities.length > 0 ? Math.max(...charities.map(c => c.score)) : 0;
  const avgScore = charities.length > 0 ? Math.round(charities.reduce((s, c) => s + c.score, 0) / charities.length) : 0;

  return (
    <div className="h-full flex flex-col" data-testid="charity-discover-view">
      {/* ─── Header ─── */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 px-6 py-5 text-white">
        <div className="flex gap-5">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-8 h-8 rounded-lg bg-white/10 backdrop-blur flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-blue-300" />
              </div>
              <div>
                <h2 className="text-lg font-bold">Discover Charities</h2>
                <p className="text-xs text-blue-200">{subtitle}</p>
              </div>
            </div>

            {/* Search */}
            <div className="relative mt-3" data-testid="discover-search">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input type="text" value={query} onChange={(e) => handleQueryChange(e.target.value)}
                placeholder={`Try "${placeholders[phIdx]}"...`}
                className="w-full pl-11 pr-10 py-2.5 bg-white/10 backdrop-blur border border-white/20 rounded-xl text-sm text-white placeholder-slate-400 focus:bg-white/15 focus:border-blue-400 focus:ring-0 transition-all"
                data-testid="discover-search-input" />
              {query && (
                <button onClick={() => { setQuery(''); fetchCharities(''); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-white/10 rounded-full">
                  <X className="w-3.5 h-3.5 text-slate-300" />
                </button>
              )}
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-1.5 mt-3" data-testid="discover-distance-filters">
              <span className="text-[10px] font-semibold text-blue-300 mr-0.5 flex items-center gap-1"><MapPin className="w-3 h-3" /></span>
              {DISTANCE_OPTIONS.map(opt => (
                <button key={opt.value} data-testid={`filter-dist-${opt.value}`} onClick={() => handleRadiusChange(opt.value)}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-all ${
                    radiusMiles === opt.value ? 'bg-blue-500 border-blue-400 text-white' : 'bg-white/5 border-white/15 text-blue-200 hover:bg-white/10'}`}>
                  {opt.label}
                </button>
              ))}
              <div className="w-px h-4 bg-white/15 mx-1" />
              <FilterChip active={filters.csr_match} onClick={() => toggleFilter('csr_match')} icon={Heart} label="CSR" color="sky" dark testId="filter-csr" />
              <FilterChip active={filters.esg_match} onClick={() => toggleFilter('esg_match')} icon={Leaf} label="ESG" color="violet" dark testId="filter-esg" />
            </div>
            <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid="discover-filters">
              {CATEGORY_OPTIONS.map(cat => (
                <FilterChip key={cat.id} active={filters.category === cat.id} onClick={() => setCategory(cat.id)} label={cat.label} color="white" dark small testId={`filter-cat-${cat.id}`} />
              ))}
            </div>
          </div>

          {/* Right panel */}
          <div className="hidden xl:block w-56 flex-shrink-0">
            <ProfileSummary bizContext={bizContext} />
          </div>
        </div>
      </div>

      {/* ─── Results ─── */}
      <div className="flex-1 overflow-y-auto bg-gradient-to-b from-gray-50 to-gray-100 p-5">
        <div className="max-w-[1400px] mx-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20" data-testid="discover-loading">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mb-4 animate-pulse">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <p className="text-sm font-medium text-gray-600">{radiusMiles > 0 ? `Searching within ${radiusMiles} miles...` : 'Searching nationwide...'}</p>
            </div>
          ) : charities.length === 0 ? (
            <div className="text-center py-20" data-testid="discover-empty">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-gray-200 flex items-center justify-center mb-4">
                <Search className="w-7 h-7 text-gray-400" />
              </div>
              <h3 className="font-bold text-gray-800 mb-1">No charities found</h3>
              <p className="text-sm text-gray-500">Try increasing the distance or adjusting your search</p>
            </div>
          ) : (
            <>
              {/* Stats bar */}
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div className="flex items-center gap-4">
                  <p className="text-sm text-gray-500">
                    <span className="font-bold text-gray-800">{charities.length}</span> charities
                    {radiusMiles > 0 && <span> within <span className="font-semibold text-blue-600">{radiusMiles} mi</span></span>}
                  </p>
                  <div className="hidden sm:flex items-center gap-3 text-xs">
                    <span className="flex items-center gap-1 px-2 py-1 bg-sky-50 text-sky-700 rounded-lg font-semibold">
                      <Award className="w-3 h-3" /> Top: {topScore}%
                    </span>
                    <span className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-lg font-semibold">
                      Avg: {avgScore}%
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* Sort */}
                  <div className="relative" ref={sortDropdownRef}>
                    <button data-testid="discover-sort-btn" onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs font-semibold text-gray-700 hover:border-gray-300 transition-all shadow-sm">
                      <currentSortOption.icon className="w-3.5 h-3.5 text-gray-400" />
                      {currentSortOption.label}
                      <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${sortDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {sortDropdownOpen && (
                      <div className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded-xl shadow-xl z-20 py-1 overflow-hidden" data-testid="discover-sort-dropdown">
                        {SORT_OPTIONS.map(opt => (
                          <button key={opt.value} data-testid={`sort-${opt.value}`} onClick={() => handleSortChange(opt.value)}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors ${
                              sortBy === opt.value ? 'bg-blue-50 text-blue-700 font-bold' : 'text-gray-700 hover:bg-gray-50'}`}>
                            <opt.icon className="w-3.5 h-3.5" />{opt.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* View toggle */}
                  <div className="flex bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm" data-testid="discover-view-toggle">
                    <button data-testid="view-list-btn" onClick={() => setViewMode('list')}
                      className={`p-2 transition-all ${viewMode === 'list' ? 'bg-slate-900 text-white' : 'text-gray-400 hover:bg-gray-50 hover:text-gray-600'}`}>
                      <List className="w-4 h-4" />
                    </button>
                    <button data-testid="view-map-btn" onClick={() => setViewMode('map')}
                      className={`p-2 transition-all ${viewMode === 'map' ? 'bg-slate-900 text-white' : 'text-gray-400 hover:bg-gray-50 hover:text-gray-600'}`}>
                      <MapIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* MAP + LIST SPLIT */}
              {viewMode === 'map' ? (
                <div className="flex gap-3" style={{ height: '600px' }} data-testid="discover-split-view">
                  <div className="w-80 flex-shrink-0 overflow-y-auto space-y-2 pr-1" data-testid="discover-map-list">
                    {charities.slice(0, visibleCount).map(charity => (
                      <CharityCard key={charity.ein} charity={charity} onSelect={setSelectedCharity} compact
                        isHighlighted={selectedCharity?.ein === charity.ein} />
                    ))}
                    {visibleCount < charities.length && (
                      <button onClick={() => setVisibleCount(prev => prev + 100)}
                        className="w-full py-2 text-xs text-blue-600 hover:bg-blue-50 rounded-lg transition-colors font-semibold">
                        Show more ({charities.length - visibleCount})
                      </button>
                    )}
                  </div>
                  <div className="flex-1 rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-testid="discover-map-view">
                    <MapContainer center={[39.8283, -98.5795]} zoom={4} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
                      <MapController charities={mappableCharities} officeCoords={officeCoords} />
                      {officeCoords?.lat && officeCoords?.lon && (
                        <Marker position={[officeCoords.lat, officeCoords.lon]} icon={officeIcon}>
                          <Tooltip permanent={false} direction="top" offset={[0, -17]}>
                            <span className="font-semibold">Your Office</span>
                          </Tooltip>
                        </Marker>
                      )}
                      {mappableCharities.slice(0, 200).map(charity => (
                        <Marker key={charity.ein} position={[charity.latitude, charity.longitude]}
                          icon={createScoreIcon(charity.score)}
                          eventHandlers={{ click: () => setSelectedCharity(charity) }}>
                          <Tooltip direction="top" offset={[0, -14]}>
                            <div className="text-xs">
                              <strong>{charity.name}</strong><br/>
                              {charity.city}, {charity.state}
                              {charity.distance_miles != null && <span> · {charity.distance_miles} mi</span>}
                            </div>
                          </Tooltip>
                        </Marker>
                      ))}
                    </MapContainer>
                  </div>
                </div>
              ) : (
                /* GRID LIST */
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="discover-results-grid">
                    {charities.slice(0, visibleCount).map(charity => (
                      <CharityCard key={charity.ein} charity={charity} onSelect={setSelectedCharity} />
                    ))}
                  </div>
                  {visibleCount < charities.length && (
                    <div className="text-center mt-6">
                      <button data-testid="discover-show-more-visible" onClick={() => setVisibleCount(prev => prev + 100)}
                        className="px-6 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 shadow-sm transition-all">
                        Show More ({charities.length - visibleCount} loaded)
                      </button>
                    </div>
                  )}
                  {visibleCount >= charities.length && hasMore && (
                    <div className="text-center mt-6">
                      <button data-testid="discover-load-more" onClick={loadMore} disabled={loadingMore}
                        className="px-6 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 shadow-sm transition-all disabled:opacity-50">
                        {loadingMore ? <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading...</span> : 'Load Next 100'}
                      </button>
                    </div>
                  )}
                  {!hasMore && charities.length > 0 && <p className="text-center text-sm text-gray-400 mt-6">All matching charities loaded</p>}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* ─── Detail Panel ─── */}
      {selectedCharity && (
        <div className="fixed inset-0 flex justify-end" style={{ zIndex: 10000 }} data-testid="discover-detail-panel">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setSelectedCharity(null)} />
          <div className="relative w-full max-w-md bg-white shadow-2xl overflow-y-auto animate-slide-in">
            {/* Colored header */}
            <div className={`bg-gradient-to-br ${
              selectedCharity.score >= 70 ? 'from-sky-600 to-blue-700' :
              selectedCharity.score >= 40 ? 'from-amber-500 to-orange-600' :
              'from-slate-600 to-slate-700'
            } px-6 py-5 text-white`}>
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0 pr-4">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-lg leading-tight">{selectedCharity.name}</h3>
                    <span className="px-1.5 py-0.5 bg-white/20 backdrop-blur rounded text-[9px] font-bold uppercase tracking-wider flex items-center gap-1">
                      <Shield className="w-2.5 h-2.5" /> 501(c)(3)
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-2 text-white/80 text-sm">
                    <MapPin className="w-3.5 h-3.5" />
                    <span>{selectedCharity.city}{selectedCharity.city && selectedCharity.state ? ', ' : ''}{selectedCharity.state}</span>
                    {selectedCharity.distance_miles != null && (
                      <span className="px-2 py-0.5 rounded-full bg-white/20 text-[11px] font-bold">{selectedCharity.distance_miles} mi</span>
                    )}
                  </div>
                </div>
                <button onClick={() => setSelectedCharity(null)} className="p-1.5 hover:bg-white/15 rounded-full transition-colors flex-shrink-0">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="mt-4 flex items-center gap-3">
                <div className="w-16 h-16 bg-white/15 backdrop-blur rounded-xl flex flex-col items-center justify-center">
                  <span className="text-2xl font-black">{selectedCharity.score}</span>
                  <span className="text-[9px] font-bold uppercase tracking-wider text-white/70">Match</span>
                </div>
                <div className="flex-1">
                  <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                    <div className="h-full bg-white rounded-full transition-all" style={{ width: `${selectedCharity.score}%` }} />
                  </div>
                  <p className="text-xs text-white/70 mt-1.5">Based on your CSR, ESG & geographic profile</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-5">
              {/* Verified Badge */}
              <div className="flex items-center gap-3 p-3 bg-sky-50 border border-sky-200 rounded-xl">
                <div className="w-9 h-9 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-sky-600" />
                </div>
                <div>
                  <p className="text-xs font-bold text-sky-800">IRS Verified Nonprofit</p>
                  <p className="text-[11px] text-sky-600">EIN: {selectedCharity.ein} &middot; Tax-exempt 501(c)(3)</p>
                </div>
              </div>

              {/* Details grid */}
              <div className="grid grid-cols-2 gap-3">
                <DetailCard icon={MapPin} label="Location" value={`${selectedCharity.city}${selectedCharity.state ? `, ${selectedCharity.state}` : ''}`} />
                {selectedCharity.distance_miles != null && <DetailCard icon={Navigation} label="Distance" value={`${selectedCharity.distance_miles} miles`} />}
                <DetailCard icon={Building2} label="Category" value={selectedCharity.category} />
                <DetailCard icon={Star} label="EIN" value={selectedCharity.ein} />
                {selectedCharity.income > 0 && <DetailCard icon={TrendingUp} label="Revenue" value={`$${selectedCharity.income >= 1e6 ? `${(selectedCharity.income/1e6).toFixed(1)}M` : selectedCharity.income.toLocaleString()}`} />}
                <DetailCard icon={DollarSign} label="Status" value="Donation Ready" />
              </div>

              {/* How Donations Work */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
                <p className="text-[10px] font-bold text-blue-800 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <CreditCard className="w-3.5 h-3.5" /> How Donations Work
                </p>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="px-2.5 py-1.5 bg-white rounded-lg text-xs font-semibold border border-gray-200 text-gray-700">Your Company</span>
                  <ArrowRight className="w-3 h-3 text-blue-400 flex-shrink-0" />
                  <span className="px-2.5 py-1.5 bg-blue-100 rounded-lg text-xs font-bold border border-blue-200 text-blue-800 flex items-center gap-1">
                    <Shield className="w-3 h-3" /> Secure Payment
                  </span>
                  <ArrowRight className="w-3 h-3 text-blue-400 flex-shrink-0" />
                  <span className="px-2.5 py-1.5 bg-sky-100 rounded-lg text-xs font-bold border border-sky-200 text-sky-800 flex items-center gap-1">
                    <Heart className="w-3 h-3" /> {selectedCharity.name.length > 20 ? selectedCharity.name.slice(0, 20) + '...' : selectedCharity.name}
                  </span>
                </div>
                <p className="text-[10px] text-blue-600 mt-2.5">Altrunic charges 0% platform fee. Every.org processes the payment, issues your tax receipt and grants the funds to the charity — card processing is 2.2% + $0.30 unless you cover it.</p>
              </div>

              {/* Reasons */}
              {selectedCharity.reasons?.length > 0 && (
                <div>
                  <p className="text-xs font-bold text-gray-500 mb-2.5 uppercase tracking-wider">Why this matches</p>
                  <div className="space-y-2">
                    {selectedCharity.reasons.map((reason, i) => {
                      const style = REASON_STYLES[reason.type] || REASON_STYLES.keyword;
                      const Icon = style.icon;
                      return (
                        <div key={i} className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl border ${style.bg} ${style.border}`}>
                          <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${style.gradient} flex items-center justify-center flex-shrink-0`}>
                            <Icon className="w-3.5 h-3.5 text-white" />
                          </div>
                          <span className={`text-sm font-medium ${style.text}`}>{reason.text}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="space-y-2.5 pt-2">
                <button data-testid="discover-donate-btn"
                  onClick={() => { if (onDonate) onDonate(selectedCharity); }}
                  className="w-full py-3.5 rounded-xl text-white font-bold text-sm flex items-center justify-center gap-2 transition-all hover:shadow-lg bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 shadow-lg shadow-sky-100">
                  <CreditCard className="w-4 h-4" /> Donate Now
                </button>
                <button data-testid="discover-start-campaign-btn"
                  onClick={() => { if (onSelectCharity) onSelectCharity(selectedCharity); setSelectedCharity(null); }}
                  className="w-full py-3 rounded-xl text-white font-bold text-sm flex items-center justify-center gap-2 transition-all hover:shadow-lg bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700">
                  <Star className="w-4 h-4" /> Start Campaign
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .animate-slide-in { animation: slideIn 0.2s ease-out; }
        .leaflet-container { font-family: inherit; }
      `}</style>
    </div>
  );
};

const FilterChip = ({ active, onClick, icon: Icon, label, color, small, dark, testId }) => {
  const darkStyles = {
    sky: active ? 'bg-sky-500 border-sky-400 text-white' : 'bg-white/5 border-white/15 text-sky-200 hover:bg-white/10',
    violet: active ? 'bg-violet-500 border-violet-400 text-white' : 'bg-white/5 border-white/15 text-violet-200 hover:bg-white/10',
    white: active ? 'bg-white text-slate-900 border-white' : 'bg-white/5 border-white/15 text-slate-300 hover:bg-white/10',
  };
  const lightStyles = {
    sky: active ? 'bg-sky-100 border-sky-400 text-sky-700' : 'bg-white border-gray-200 text-gray-600 hover:border-sky-300',
    violet: active ? 'bg-violet-100 border-violet-400 text-violet-700' : 'bg-white border-gray-200 text-gray-600 hover:border-violet-300',
    gray: active ? 'bg-gray-800 border-gray-800 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-400',
  };
  const styles = dark ? darkStyles : lightStyles;
  return (
    <button data-testid={testId} onClick={onClick}
      className={`inline-flex items-center gap-1 border rounded-full transition-all ${styles[color] || styles.white} ${small ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-[11px]'} font-semibold`}>
      {Icon && <Icon className={small ? 'w-2.5 h-2.5' : 'w-3 h-3'} />}{label}
    </button>
  );
};

const DetailCard = ({ icon: Icon, label, value }) => (
  <div className="bg-gray-50 rounded-xl p-3">
    <div className="flex items-center gap-1.5 mb-1">
      <Icon className="w-3 h-3 text-gray-400" />
      <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{label}</span>
    </div>
    <p className="text-sm font-bold text-gray-900 truncate">{value || '—'}</p>
  </div>
);

export default CharityDiscoverView;
